import React from 'react';
import { Activity } from 'lucide-react';

// Catégorisation standard push / pull / jambes / core à partir du `muscle_group`
// (labels FR déjà stockés sur chaque exercice, cf. exercise_library_v2_data.py).
const CATEGORY_MAP = {
  'Pectoraux': 'push', 'Épaules': 'push', 'Triceps': 'push',
  'Dos': 'pull', 'Biceps': 'pull', 'Trapèzes': 'pull', 'Avant-bras': 'pull', 'Lombaires': 'pull',
  'Quadriceps': 'legs', 'Ischio-jambiers': 'legs', 'Fessiers': 'legs', 'Mollets': 'legs', 'Adducteurs / Abducteurs': 'legs',
  'Abdominaux': 'core',
};

const CATEGORY_LABELS = {
  push: ['Poussée', 'Push'], pull: ['Tirage', 'Pull'], legs: ['Jambes', 'Legs'], core: ['Core', 'Core'], other: ['Autre', 'Other'],
};

const CATEGORY_COLORS = {
  push: 'bg-orange-500', pull: 'bg-sky-500', legs: 'bg-emerald-500', core: 'bg-purple-500', other: 'bg-zinc-500',
};

const computeCoherence = (sessions) => {
  const counts = { push: 0, pull: 0, legs: 0, core: 0, other: 0 };
  let total = 0;
  (sessions || []).forEach((s) => {
    (s.exercises || []).forEach((ex) => {
      const cat = CATEGORY_MAP[ex.muscle_group] || 'other';
      const sets = ex.sets_count || 1;
      counts[cat] += sets;
      total += sets;
    });
  });
  return { counts, total };
};

/**
 * Score de cohérence d'un programme : équilibre push/pull + volume par groupe musculaire.
 * Réutilisé à l'étape "Récap" de la création (MyProgramBuilder) et sur l'aperçu d'un
 * programme déjà créé (ProgramOverview) — même logique de calcul, purement côté client
 * (les exercices sélectionnés portent déjà leur `muscle_group` et `sets_count`).
 */
export const ProgramCoherenceScore = ({ sessions, en }) => {
  const L = (fr, eng) => (en ? eng : fr);
  const { counts, total } = computeCoherence(sessions);
  if (!total) return null;

  const pushPct = Math.round((counts.push / total) * 100);
  const pullPct = Math.round((counts.pull / total) * 100);
  const imbalance = Math.abs(pushPct - pullPct);

  let verdict, verdictColor;
  if (counts.push + counts.pull < 4) {
    verdict = L('Pas assez de données', 'Not enough data');
    verdictColor = 'text-zinc-400';
  } else if (imbalance <= 15) {
    verdict = L('Équilibre push/pull excellent', 'Great push/pull balance');
    verdictColor = 'text-emerald-400';
  } else if (imbalance <= 30) {
    verdict = L('Équilibre correct, à surveiller', 'Decent balance, worth watching');
    verdictColor = 'text-yellow-400';
  } else {
    verdict = pushPct > pullPct
      ? L('Trop de poussée, pas assez de tirage', 'Too much push, not enough pull')
      : L('Trop de tirage, pas assez de poussée', 'Too much pull, not enough push');
    verdictColor = 'text-orange-400';
  }

  return (
    <div className="rounded-2xl bg-white/[0.035] border border-white/10 p-4" data-testid="program-coherence-score">
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <h4 className="text-white font-bold text-sm flex items-center gap-2">
          <Activity className="w-4 h-4 text-primary" /> {L('Score de cohérence du programme', 'Program coherence score')}
        </h4>
        <span className={`text-xs font-bold ${verdictColor}`} data-testid="coherence-verdict">{verdict}</span>
      </div>

      <div className="mb-3">
        <div className="flex items-center justify-between text-[11px] text-zinc-400 mb-1">
          <span>{L('Poussée', 'Push')} {pushPct}%</span>
          <span>{L('Tirage', 'Pull')} {pullPct}%</span>
        </div>
        <div className="h-2.5 rounded-full bg-zinc-800 overflow-hidden flex" data-testid="coherence-push-pull-bar">
          <div className="h-full bg-orange-500" style={{ width: `${pushPct}%` }} />
          <div className="h-full bg-sky-500" style={{ width: `${pullPct}%` }} />
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {['push', 'pull', 'legs', 'core'].map((cat) => {
          const pct = Math.round((counts[cat] / total) * 100);
          return (
            <div key={cat} className="text-center" data-testid={`coherence-cat-${cat}`}>
              <div className={`w-full h-1.5 rounded-full mb-1 ${CATEGORY_COLORS[cat]}`} style={{ opacity: pct ? 1 : 0.15 }} />
              <p className="text-[10px] text-zinc-500">{CATEGORY_LABELS[cat][en ? 1 : 0]}</p>
              <p className="text-xs font-bold text-white">{counts[cat]} {L('séries', 'sets')}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ProgramCoherenceScore;
